import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  ShoppingCart,
  Users,
  Settings,
  ChevronLeft,
  ChevronRight,
  Code,
  ScrollText,
  PlusCircle,
  Activity,
  Mail,
  Shield,
  X,
} from 'lucide-react';
import { clsx } from 'clsx';

interface SidebarProps {
  isOpen: boolean;
  onToggle: () => void;
  onMobileClose?: () => void;
  isMobile?: boolean;
}

const navItems = [
  { to: '/', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/orders', icon: ShoppingCart, label: 'Orders' },
  { to: '/orders/new', icon: PlusCircle, label: 'New Order', indent: true },
  { to: '/processing', icon: Activity, label: 'Processing', indent: true },
  { to: '/emails', icon: Mail, label: 'Emails', indent: true },
  { to: '/hiring', icon: Users, label: 'Hiring' },
  { to: '/developer', icon: Code, label: 'Developer' },
  { to: '/logs', icon: ScrollText, label: 'Logs' },
  { to: '/settings', icon: Settings, label: 'Settings' },
  { to: '/settings/allowlist', icon: Shield, label: 'Allowlist', indent: true },
];

export default function Sidebar({ isOpen, onToggle, onMobileClose, isMobile }: SidebarProps) {
  // On mobile, sidebar is always expanded
  const effectiveIsOpen = isMobile ? true : isOpen;

  const handleNavClick = () => {
    // Close mobile menu when clicking a link
    if (isMobile && onMobileClose) {
      onMobileClose();
    }
  };

  return (
    <aside
      className={clsx(
        'bg-white border-r border-gray-200 flex flex-col h-full transition-all duration-300 ease-in-out',
        effectiveIsOpen ? 'w-64' : 'w-20'
      )}
    >
      {/* Logo section */}
      <div className="h-14 sm:h-16 flex items-center justify-between px-4 border-b border-gray-200">
        <div className="flex items-center gap-3">
          {/* Perfectted Logo */}
          <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-perfectted-primary flex items-center justify-center flex-shrink-0">
            <span className="text-white font-bold text-base sm:text-lg">P</span>
          </div>
          {effectiveIsOpen && (
            <div className="flex flex-col">
              <span className="font-bold text-gray-900 text-base sm:text-lg leading-tight">PerfectTed</span>
              <span className="text-xs text-gray-500">Operations</span>
            </div>
          )}
        </div>

        {/* Mobile close button */}
        {isMobile && onMobileClose && (
          <button
            onClick={onMobileClose}
            className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors lg:hidden"
            aria-label="Close menu"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-3 sm:p-4 space-y-1 overflow-y-auto">
        {navItems.map(({ to, icon: Icon, label, indent }) => (
          <NavLink
            key={to}
            to={to}
            end={to === '/' || to === '/orders'}
            onClick={handleNavClick}
            className={({ isActive }) =>
              clsx(
                'flex items-center gap-3 px-3 py-2.5 rounded-lg font-medium transition-all duration-200',
                indent && effectiveIsOpen && 'ml-4',
                isActive
                  ? 'bg-perfectted-light text-perfectted-dark'
                  : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
              )
            }
          >
            <Icon className={clsx('flex-shrink-0', indent ? 'w-4 h-4' : 'w-5 h-5')} />
            {effectiveIsOpen && <span className={clsx('truncate', indent ? 'text-sm' : '')}>{label}</span>}
          </NavLink>
        ))}
      </nav>

      {/* Collapse toggle button - only on desktop */}
      {!isMobile && (
        <div className="p-3 sm:p-4 border-t border-gray-200 hidden lg:block">
          <button
            onClick={onToggle}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition-colors"
          >
            {effectiveIsOpen ? (
              <>
                <ChevronLeft className="w-5 h-5" />
                <span className="text-sm font-medium">Collapse</span>
              </>
            ) : (
              <ChevronRight className="w-5 h-5" />
            )}
          </button>
        </div>
      )}

      {/* Version info */}
      {effectiveIsOpen && (
        <div className="px-4 pb-3 sm:pb-4">
          <div className="text-xs text-gray-400 text-center">
            v1.0.0
          </div>
        </div>
      )}
    </aside>
  );
}
